// src/components/Product/ProductGrid.jsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiStar,
  FiShoppingCart,
  FiPlus,
  FiMinus,
  FiTag,
  FiEye,
  FiHeart,
  FiChevronRight,
} from "react-icons/fi";
import { useCart } from "../../context/CartContext";
import { sanitizeImageUrl } from "../../utils/imageUtils";
import QuickViewModal from "./QuickViewModal";
import ProductDeliveryPromo from "./ProductDeliveryPromo";

const PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300' viewBox='0 0 300 300'%3E%3Crect width='300' height='300' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='45%25' font-family='Arial' font-size='48' fill='%23ddd' text-anchor='middle' dy='.3em'%3E📦%3C/text%3E%3Ctext x='50%25' y='60%25' font-family='Arial' font-size='14' fill='%23999' text-anchor='middle' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E";

const isTouchDevice = () => {
  if (typeof window === "undefined") return false;
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
};

const ProductCard = ({ product, onAddToCart, promotion, addingProductId }) => {
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [showQuickView, setShowQuickView] = useState(false);
  const [canHover, setCanHover] = useState(false);

  useEffect(() => {
    setCanHover(!isTouchDevice());
  }, []);

  // Extract images
  const getProductImages = () => {
    if (!product) return [];
    const collectedImages = [];

    const addImage = (value) => {
      if (!value) return;
      if (Array.isArray(value)) {
        value.forEach(addImage);
        return;
      }
      if (typeof value === "object" && value !== null) {
        if (value.url) collectedImages.push(value.url);
        return;
      }
      if (typeof value !== "string") return;

      const trimmed = sanitizeImageUrl(value);
      if (!trimmed) return;

      if (trimmed.startsWith("[")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            parsed.forEach(addImage);
            return;
          }
        } catch (error) {
          console.warn("Could not parse image array string", error);
        }
      }

      if (trimmed.includes("|")) {
        trimmed.split("|").forEach(addImage);
        return;
      }

      if (trimmed.includes(",") && trimmed.includes("http")) {
        const parts = trimmed.split(",").map((part) => part.trim());
        if (parts.length > 1) {
          parts.forEach(addImage);
          return;
        }
      }

      collectedImages.push(trimmed);
    };

    const featuredVariation = product.variations?.find(
      (variation) => variation.IsActive !== false,
    );
    if (featuredVariation) {
      addImage(featuredVariation.ImageUrl);
      addImage(featuredVariation.VariationImages);
    }

    if (product.images && Array.isArray(product.images)) {
      product.images.forEach(addImage);
    }

    if (collectedImages.length === 0) {
      addImage(product.ImageUrl);
      addImage(product.Images);
      addImage(product.ImageUrls);
      addImage(product.AdditionalImages);
      addImage(product.ImageData);
    }

    const filtered = collectedImages.filter(
      (image, index, self) => image && self.indexOf(image) === index,
    );

    return filtered.length === 0 ? [PLACEHOLDER_IMAGE] : filtered;
  };

  const images = getProductImages();
  const hasMultipleImages = images.length > 1;

  useEffect(() => {
    if (!hasMultipleImages || isHovering || showQuickView) return;
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [hasMultipleImages, images.length, isHovering, showQuickView]);

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [product?.ProductID]);

  const handleImageError = (e) => {
    setImgError(true);
    e.target.src = PLACEHOLDER_IMAGE;
  };

  const getImageUrl = (image) => {
    if (!image || imgError) return PLACEHOLDER_IMAGE;
    if (
      typeof image === "string" &&
      (image.startsWith("/uploads/") || image.startsWith("uploads/"))
    ) {
      return `${process.env.REACT_APP_API_URL || ""}${image}`;
    }
    return image;
  };

  const goToSlide = (index, e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex(index);
  };

  const nextSlide = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const isAddingThisProduct =
    addingProductId === product?.ProductID || isAdding;

  // Price calculations
  const getDiscountedPrice = () => {
    if (!promotion) return null;
    const originalPrice = product?.SellingPrice || 0;
    const discountValue = parseFloat(promotion.DiscountValue) || 0;

    if (promotion.DiscountType === "Percentage") {
      const discountAmount = (originalPrice * discountValue) / 100;
      return Math.round((originalPrice - discountAmount) * 100) / 100;
    } else if (promotion.DiscountType === "FixedAmount") {
      return Math.max(
        0,
        Math.round((originalPrice - discountValue) * 100) / 100,
      );
    }
    return null;
  };

  const discountedPrice = getDiscountedPrice();
  const hasPromotion =
    !!promotion &&
    discountedPrice !== null &&
    discountedPrice < (product?.SellingPrice || 0);
  const finalPrice = hasPromotion
    ? discountedPrice
    : product?.SellingPrice || 0;
  const originalPrice = product?.SellingPrice || 0;
  const discountApplied = hasPromotion ? originalPrice - finalPrice : 0;

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!product || product.StockQuantity === 0) return;

    setIsAdding(true);
    try {
      const productId = product.ProductID || product.id;

      if (onAddToCart) {
        await onAddToCart(product, quantity);
      } else {
        await addToCart(productId, quantity, {
          name: product.Name,
          price: finalPrice,
          originalPrice: originalPrice,
          imageUrl: getImageUrl(images[0]),
          sku: product.SKU,
          promotion: promotion,
          discountApplied: discountApplied,
        });
      }
      setQuantity(1);
    } catch (error) {
      console.error("Error adding to cart:", error);
    } finally {
      setIsAdding(false);
    }
  };

  const increaseQuantity = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (quantity < (product?.StockQuantity || 99)) {
      setQuantity(quantity + 1);
    }
  };

  const decreaseQuantity = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const openQuickView = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (showQuickView) return;

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    setIsHovering(false);
    setShowQuickView(true);
  };

  const rating = product.reviewStats?.rating || 0;
  const reviewCount = product.reviewStats?.count || 0;

  const discount =
    product?.CostPrice && product?.SellingPrice < product?.CostPrice
      ? Math.round(
          ((product.CostPrice - product.SellingPrice) / product.CostPrice) *
            100,
        )
      : 0;

  const promotionDiscountPercent =
    hasPromotion && promotion.DiscountType === "Percentage"
      ? Math.round(parseFloat(promotion.DiscountValue))
      : 0;

  const inStock = (product?.StockQuantity ?? 1) > 0;

  const renderStars = () => {
    const rounded = Math.round(rating);
    return (
      <div className="pc-stars">
        {[1, 2, 3, 4, 5].map((star) => (
          <FiStar
            key={star}
            size={16}
            className={`pc-star ${star <= rounded ? "filled" : ""}`}
          />
        ))}
      </div>
    );
  };

  if (!product || !product.ProductID) {
    return null;
  }

  return (
    <>
      <motion.div
        className="product-card-rich"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        whileHover={canHover ? { y: -6 } : undefined}
        onMouseEnter={() => canHover && setIsHovering(true)}
        onMouseLeave={() => canHover && setIsHovering(false)}
      >
        {/* ── Image area ── */}
        <div
          className="pc-image-area"
          onClick={openQuickView}
          style={{ cursor: "pointer" }}
        >
          {hasPromotion && (
            <div className="pc-badge pc-badge-bestseller">
              <FiTag size={12} />
              {promotionDiscountPercent > 0
                ? `${promotionDiscountPercent}% OFF`
                : "SALE"}
            </div>
          )}
          {discount > 0 && !hasPromotion && (
            <div className="pc-badge pc-badge-bestseller">
              <FiTag size={12} />-{discount}%
            </div>
          )}
          {product.isNew && <div className="pc-badge pc-badge-new">New</div>}

          <button
            className={`pc-wishlist ${isWishlisted ? "active" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsWishlisted((v) => !v);
            }}
            aria-label="Add to wishlist"
          >
            <FiHeart />
          </button>

          <div className="pc-image-slider">
            <div
              className="pc-image-track"
              style={{
                transform: `translateX(-${currentImageIndex * 100}%)`,
              }}
            >
              {images.map((imgUrl, index) => (
                <img
                  key={index}
                  src={getImageUrl(imgUrl)}
                  alt={`${product.Name || "Product"} - ${index + 1}`}
                  loading="lazy"
                  className="pc-image"
                  onError={handleImageError}
                />
              ))}
            </div>

            {hasMultipleImages && (
              <button
                className="pc-next-btn"
                onClick={nextSlide}
                aria-label="Next image"
              >
                <FiChevronRight />
              </button>
            )}
          </div>

          {hasMultipleImages && (
            <div className="pc-thumbnails">
              {images.slice(0, 4).map((img, idx) => (
                <button
                  key={idx}
                  className={`pc-thumb ${idx === currentImageIndex ? "active" : ""}`}
                  onClick={(e) => goToSlide(idx, e)}
                  aria-label={`View image ${idx + 1}`}
                >
                  <img
                    src={getImageUrl(img)}
                    alt=""
                    onError={handleImageError}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Content area ── */}
        <div className="pc-content">
          {product.CategoryName && (
            <div className="pc-eyebrow">
              {product.CategoryName.toUpperCase()}
            </div>
          )}

          <div
            className="pc-title-link"
            onClick={openQuickView}
            style={{ cursor: "pointer" }}
          >
            <h3 className="pc-title">{product.Name || "Unnamed Product"}</h3>
          </div>

          {product.Description && (
            <p className="pc-description">
              {product.Description.length > 120
                ? `${product.Description.substring(0, 120)}...`
                : product.Description}
            </p>
          )}

          <div className="pc-rating-row">
            {renderStars()}
            {rating > 0 ? (
              <>
                <span className="pc-rating-value">{rating.toFixed(1)}</span>
                <span className="pc-rating-count">
                  ({reviewCount} {reviewCount === 1 ? "review" : "reviews"})
                </span>
              </>
            ) : (
              <span className="pc-rating-count">No reviews yet</span>
            )}
          </div>

          <div className="pc-price-row">
            <span className="pc-price-current">
              R{(finalPrice || 0).toFixed(2)}
            </span>
            {(hasPromotion || discount > 0) && (
              <>
                <span className="pc-price-original">
                  R{(originalPrice || 0).toFixed(2)}
                </span>
                {promotionDiscountPercent > 0 && (
                  <span className="pc-discount-badge">
                    {promotionDiscountPercent}% OFF
                  </span>
                )}
              </>
            )}
          </div>
          <ProductDeliveryPromo />

          {hasPromotion && promotion.Code && (
            <div className="pc-promo-code">
              Use code: <strong>{promotion.Code}</strong>
            </div>
          )}

          {/* ✅ Bottom cluster — pinned to bottom via margin-top: auto */}
          <div className="pc-bottom-cluster">
            <div className="pc-quantity-row">
              <div className="pc-quantity-controls">
                <button
                  className="pc-qty-btn"
                  onClick={decreaseQuantity}
                  disabled={quantity <= 1 || !inStock || isAddingThisProduct}
                  aria-label="Decrease quantity"
                >
                  <FiMinus size={14} />
                </button>
                <span className="pc-qty-value">{quantity}</span>
                <button
                  className="pc-qty-btn"
                  onClick={increaseQuantity}
                  disabled={
                    quantity >= (product.StockQuantity || 99) ||
                    !inStock ||
                    isAddingThisProduct
                  }
                  aria-label="Increase quantity"
                >
                  <FiPlus size={14} />
                </button>
              </div>
            </div>

            <div className="pc-actions">
              <button
                className="pc-btn-cart"
                onClick={handleAddToCart}
                disabled={!inStock || isAddingThisProduct}
              >
                <FiShoppingCart />
                <span>
                  {isAddingThisProduct
                    ? "Adding..."
                    : !inStock
                      ? "Out of Stock"
                      : "Add to Cart"}
                </span>
              </button>
              <button className="pc-btn-details" onClick={openQuickView}>
                <FiEye />
                <span>View Details</span>
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {showQuickView && (
          <QuickViewModal
            product={product}
            onClose={() => setShowQuickView(false)}
            onAddToCart={onAddToCart}
          />
        )}
      </AnimatePresence>
    </>
  );
};

const ProductGrid = ({
  products,
  loading,
  onAddToCart,
  promotions,
  getProductPromotion,
  addingProductId,
  gridConfig,
  interstitials = [],
  viewMode = "grid",
}) => {
  const safeProducts = Array.isArray(products) ? products : [];
  const interstitialsByPosition = new Map();
  interstitials.forEach((item) => {
    const afterProducts = Number(item.afterProducts);
    if (
      !Number.isInteger(afterProducts) ||
      afterProducts < 1 ||
      afterProducts > safeProducts.length ||
      !item.content
    ) {
      return;
    }
    const existing = interstitialsByPosition.get(afterProducts) || [];
    existing.push(item);
    interstitialsByPosition.set(afterProducts, existing);
  });
  const gridStyle = {
    "--storefront-desktop-columns": gridConfig?.desktopColumns || 4,
    "--storefront-mobile-columns": gridConfig?.mobileColumns || 2,
    ...(viewMode === "list" ? { gridTemplateColumns: "1fr" } : {}),
  };
  const gridClassName = `customer-product-grid storefront-product-grid${
    viewMode === "list" ? " list-view" : ""
  }`;

  if (loading) {
    return (
      <div className={gridClassName} style={gridStyle}>
        {[...Array(8)].map((_, i) => (
          <div key={i} className="skeleton-card">
            <div className="skeleton-image"></div>
            <div className="skeleton-text"></div>
            <div className="skeleton-text short"></div>
            <div className="skeleton-text price"></div>
            <div className="skeleton-button"></div>
          </div>
        ))}
      </div>
    );
  }

  if (safeProducts.length === 0) {
    return (
      <motion.div
        className="empty-state"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="empty-icon">🔍</div>
        <h3>No products found</h3>
        <p>Try adjusting your filters or check back later</p>
      </motion.div>
    );
  }

  return (
    <div className={gridClassName} style={gridStyle}>
      {safeProducts.map((product, index) => {
        const promotion = getProductPromotion
          ? getProductPromotion(product.ProductID, product.CategoryID)
          : null;
        const productId = product.ProductID || product.id || `product-${index}`;
        const productCard = (
          <ProductCard
            key={productId}
            product={product}
            onAddToCart={onAddToCart}
            promotion={promotion}
            addingProductId={addingProductId}
          />
        );
        const content = interstitialsByPosition.get(index + 1) || [];

        return (
          <React.Fragment key={productId}>
            {productCard}
            {content.map((item) => (
              <div className="storefront-product-interstitial" key={item.id}>
                {item.content}
              </div>
            ))}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default ProductGrid;
